import type { TAppConfig } from "~ui/@application/configs/config-schema.ts";

import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { bootstrapSession } from "~application/auth/usecases/auth-usecases.ts";
import { VOLTEXPLOIT_REQUIRED_ACCESS } from "~application/constants/access.constant.ts";
import { ERouteName } from "~application/constants/routes.constant.ts";
import { useServices } from "~ui/@application/provider.ts";
import { useOperatorStore } from "~ui/@core/stores/auth/operator-store.ts";

export type TVoltexploitRouteMeta = {
  hideInBreadcrumb?: boolean;
};
export type TVoltexploitRouterRecordRaw = RouteRecordRaw & {
  meta?: TVoltexploitRouteMeta;
};

const routes: Readonly<TVoltexploitRouterRecordRaw[]> = [
  {
    path: "/authentication",
    name: ERouteName.AUTHENTICATION,
    redirect: { name: ERouteName.AUTHENTICATION__LOGIN },
    component: () => import("~ui/@core/layouts/voltexploit-login-layout.vue"),
    meta: {},
    children: [
      {
        path: "/authentication/login",
        name: ERouteName.AUTHENTICATION__LOGIN,
        component: () => import("~ui/@modules/authentication/views/authentication-login-view.vue"),
      },
    ],
  },
  {
    path: "/",
    name: ERouteName.ROOT,
    redirect: { name: ERouteName.SEARCH_SITE },
    component: () => import("~ui/@core/layouts/voltexploit-layout.vue"),
    meta: {
      hideInBreadcrumb: true,
    },
    children: [
      {
        path: "/",
        name: ERouteName.SEARCH_SITE,
        component: () => import("~ui/@modules/home/views/search-site.vue"),
      },
      {
        path: "/devices",
        component: () => import("~ui/@modules/devices/views/device-root-view.vue"),
        children: [
          {
            path: "modulators/:modulatorHwId",
            name: ERouteName.DEVICES_MODULATOR_DETAILS,
            component: () => import("~ui/@modules/devices/views/modulators/modulator-details-view.vue"),
          },
          {
            path: "pilots/:pilotHwId",
            name: ERouteName.DEVICES_PILOTS_DETAILS,
            component: () => import("~ui/@modules/devices/views/pilots/pilot-details-view.vue"),
          },
          {
            path: "third-party-sensors/:thirdPartySensorHwId",
            name: ERouteName.DEVICES_THIRD_PARTY_SENSORS_DETAILS,
            component: () =>
              import("~ui/@modules/devices/views/third-party-sensors/third-party-sensor-details-view.vue"),
          },
        ],
      },
      {
        path: "/sites/:siteId",
        name: ERouteName.SITES_ROOT,
        component: () => import("~ui/@modules/sites/views/site-root-view.vue"),
        redirect: { name: ERouteName.SITES_OVERVIEW },
        meta: {},
        children: [
          {
            path: "overview",
            name: ERouteName.SITES_OVERVIEW,
            component: () => import("~ui/@modules/sites/views/overview/site-overview-view.vue"),
          },
          {
            path: "quality-control",
            name: ERouteName.SITES_QUALITY_CONTROL,
            component: () => import("~ui/@modules/sites/views/quality-control/site-quality-control-view.vue"),
          },
          {
            path: "devices",
            name: ERouteName.SITES_DEVICES,
            component: () => import("~ui/@modules/sites/views/devices/site-devices-view.vue"),
          },
          {
            path: "gallery",
            name: ERouteName.SITES_GALLERY,
            component: () => import("~ui/@modules/sites/views/gallery/site-gallery-view.vue"),
          },
          {
            path: "working-condition",
            name: ERouteName.SITES_WORKING_CONDITION,
            redirect: {
              name: ERouteName.SITES_WORKING_CONDITION__ACQUISITION,
            },
            children: [
              {
                path: "acquisition",
                name: ERouteName.SITES_WORKING_CONDITION__ACQUISITION,
                component: () =>
                  import("~ui/@modules/sites/views/working-condition/site-working-condition-acquisition-view.vue"),
              },
              {
                path: "modulation",
                name: ERouteName.SITES_WORKING_CONDITION__MODULATION,
                component: () =>
                  import("~ui/@modules/sites/views/working-condition/site-working-condition-modulation-view.vue"),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    component: () => import("~ui/@modules/errors/views/error-404.vue"),
  },
];

let APPLICATION_BOOTSTRAPPED = false;

export function createVoltexploitRouter(appConfig: TAppConfig) {
  void appConfig;

  const router = createRouter({
    routes: routes,
    history: createWebHistory(),
  });

  router.beforeEach(async (to, _from, next) => {
    const operatorStore = useOperatorStore();
    const {
      repositories: { authenticationRepository, tokenProvider },
    } = useServices();

    async function initializeOperatorStore() {
      const maybe = await bootstrapSession({
        requiredAccess: VOLTEXPLOIT_REQUIRED_ACCESS,
        tokenProvider,
        authenticationRepository,
      });

      if (maybe.success) {
        operatorStore.saveOperator({
          operator: maybe.value.operator,
          accesses: maybe.value.rights,
        });
      }
    }

    if (!APPLICATION_BOOTSTRAPPED && !operatorStore.isLoggedIn) {
      await initializeOperatorStore();
      APPLICATION_BOOTSTRAPPED = true;
    }

    if (to.name === ERouteName.AUTHENTICATION__LOGIN) {
      if (operatorStore.isLoggedIn) {
        return next({ name: ERouteName.ROOT });
      }

      return next();
    }

    if (!operatorStore.isLoggedIn) {
      return next({ name: ERouteName.AUTHENTICATION__LOGIN });
    }

    next();
  });

  return router;
}
